import { useNavigate } from "react-router-dom";
import { Sparkles, TrendingUp, Globe2, Crown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageShell from "@/components/premium/PageShell";
import aelyraImg from "@/assets/instructor-aelyra.png";
import caelionImg from "@/assets/instructor-caelion.png";
import vaelorImg from "@/assets/instructor-vaelor.png";

type Guide = {
  name: string;
  title: string;
  pillar: string;
  image: string;
  accent: string;
  glow: string;
  description: string;
  personality: string;
  topics: string[];
  icon: React.ComponentType<{ className?: string }>;
};

const GUIDES: Guide[] = [
  {
    name: "Aelyra Solari",
    title: "Finance Instructor",
    pillar: "Finance",
    image: aelyraImg,
    accent: "text-primary",
    glow: "shadow-[0_0_60px_hsl(var(--primary)/0.35)] border-primary/40",
    description:
      "Aelyra helps learners master the foundations of money — from budgeting and saving to credit, investing, and responsible financial decision-making.",
    personality:
      "Calm, precise, and deeply practical, Aelyra teaches students how to make money decisions before real life makes those lessons expensive.",
    topics: [
      "Personal finance",
      "Budgeting & saving",
      "Banking",
      "Credit & debt",
      "Investing basics",
      "Taxes & insurance",
      "Financial recovery",
      "Scams & fraud",
      "Vehicle ownership",
    ],
    icon: TrendingUp,
  },
  {
    name: "Caelion Veyr",
    title: "Economics Instructor",
    pillar: "Economics",
    image: caelionImg,
    accent: "text-accent",
    glow: "shadow-[0_0_60px_hsl(var(--accent)/0.35)] border-accent/40",
    description:
      "Caelion Veyr teaches learners how money moves through systems — from markets and incentives to inflation, policy, and the economic forces shaping everyday life.",
    personality:
      "Strategic and systems-minded, Caelion Veyr helps students see the hidden patterns behind financial decisions.",
    topics: [
      "Macroeconomics",
      "Markets",
      "Inflation",
      "Supply & demand",
      "Incentives",
      "Economic systems",
      "Company case studies",
      "Policy & systems thinking",
    ],
    icon: Globe2,
  },
  {
    name: "Headmaster Vaelor",
    title: "Entrepreneurship Instructor",
    pillar: "Entrepreneurship",
    image: vaelorImg,
    accent: "text-secondary",
    glow: "shadow-[0_0_60px_hsl(var(--secondary)/0.35)] border-secondary/40",
    description:
      "Headmaster Vaelor guides learners through ownership, entrepreneurship, value creation, leadership, and long-term legacy building.",
    personality:
      "Disciplined and visionary, Vaelor teaches students that wealth is not only earned — it is built, protected, and passed forward with wisdom.",
    topics: [
      "Entrepreneurship",
      "Business fundamentals",
      "Startups & expansion",
      "Ownership & leadership",
      "Value creation",
      "Career & income strategy",
      "Real estate investing",
      "Long-term wealth",
      "Legacy thinking",
    ],
    icon: Crown,
  },
];

const PILLARS = [
  { name: "Finance", guide: "Aelyra", blurb: "Learn how to manage and grow money.", accent: "from-primary/30 to-primary/5", text: "text-primary" },
  { name: "Economics", guide: "Caelion Veyr", blurb: "Understand the systems that shape money, markets, and opportunity.", accent: "from-accent/30 to-accent/5", text: "text-accent" },
  { name: "Entrepreneurship", guide: "Headmaster Vaelor", blurb: "Create value, build ownership, and think like a builder.", accent: "from-secondary/30 to-secondary/5", text: "text-secondary" },
];

const GuidesPage = () => {
  const navigate = useNavigate();

  return (
    <PageShell className="space-y-16">
      <h1 className="sr-only">The Instructors of HeirWise</h1>
        {/* Hero */}
        <section className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-black via-[hsl(260_35%_7%)] to-[hsl(225_40%_6%)] p-8 sm:p-12">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-secondary/15 blur-3xl" />
          <div className="relative space-y-4 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs font-heading text-primary uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" /> Luminar Collective
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-5xl text-foreground leading-tight">
              Meet the Instructors of HeirWise
            </h2>
            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
              The Luminar Collective leads students through the three pillars of financial mastery: finance, economics, and entrepreneurship.
            </p>
            <p className="text-sm text-foreground/60">
              Each guide helps students see money, systems, and ownership through a different lens.
            </p>
          </div>
        </section>

        {/* Instructor cards */}
        <section className="space-y-6">
          {GUIDES.map((g) => {
            const Icon = g.icon;
            return (
              <article
                key={g.name}
                className={`relative overflow-hidden rounded-3xl border bg-gradient-to-br from-card/80 via-card/60 to-background/80 backdrop-blur-xl ${g.glow}`}
              >
                <div className="grid md:grid-cols-[280px_1fr] gap-0">
                  <div className="relative aspect-square md:aspect-auto bg-black/40 overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${g.pillar === "Finance" ? "from-primary/20" : g.pillar === "Economics" ? "from-accent/20" : "from-secondary/20"} to-transparent`} />
                    <img src={g.image} alt={g.name} className="relative h-full w-full object-cover object-top" />
                  </div>
                  <div className="p-6 sm:p-8 space-y-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className={`inline-flex items-center gap-2 text-xs font-heading uppercase tracking-widest ${g.accent} mb-2`}>
                          <Icon className="h-3.5 w-3.5" /> {g.pillar}
                        </div>
                        <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-foreground">{g.name}</h3>
                        <p className="text-sm text-foreground/70 mt-1">{g.title}</p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-foreground/85 leading-relaxed">{g.description}</p>
                    <p className="text-sm italic text-foreground/65 border-l-2 border-primary/40 pl-4">
                      {g.personality}
                    </p>
                    <div className="space-y-2">
                      <p className="text-xs font-heading uppercase tracking-widest text-foreground/60">Teaching areas</p>
                      <div className="flex flex-wrap gap-2">
                        {g.topics.map((t) => (
                          <span key={t} className="text-xs px-2.5 py-1 rounded-full border border-border/60 bg-card/60 text-foreground/80">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button onClick={() => navigate("/curriculum")} variant="outline" className="gap-2">
                      Explore Lessons <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </section>

        {/* Three pillars */}
        <section className="space-y-6">
          <div className="text-center space-y-2">
            <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-foreground">The Three Pillars</h3>
            <p className="text-sm text-foreground/70">Every lesson in HeirWise flows from one of these three foundations.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {PILLARS.map((p) => (
              <div key={p.name} className={`rounded-2xl border border-border/60 bg-gradient-to-br ${p.accent} p-6 backdrop-blur-xl`}>
                <p className={`text-xs font-heading uppercase tracking-widest ${p.text}`}>Pillar</p>
                <h4 className="font-heading font-bold text-xl text-foreground mt-1">{p.name}</h4>
                <p className="text-sm text-foreground/80 mt-3 leading-relaxed">{p.blurb}</p>
                <p className="text-xs text-foreground/60 mt-4">Guide: <span className="text-foreground/90 font-medium">{p.guide}</span></p>
              </div>
            ))}
          </div>
        </section>

        {/* Luminar Collective lore */}
        <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-black via-[hsl(260_30%_7%)] to-[hsl(225_35%_6%)] p-8 sm:p-12 text-center">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 h-60 w-[80%] rounded-full bg-primary/10 blur-3xl" />
          <div className="relative space-y-4 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs font-heading text-primary uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" /> Academy of Continuance
            </div>
            <h3 className="font-heading font-extrabold text-2xl sm:text-4xl text-foreground">The Luminar Collective</h3>
            <p className="text-base text-foreground/80 leading-relaxed">
              The Luminar Collective is the guiding order behind HeirWise — a group of mentors dedicated to helping the next generation develop discipline, wisdom, stewardship, foresight, and financial mastery.
            </p>
          </div>
        </section>
    </PageShell>
  );
};

export default GuidesPage;